import { type JanusCtx, type Row, ec, ecId, ecLean, handBack, insertRoll, itemField, record, refuse, requiredInt, upper, isInventoryRow } from '../context.js';
import { type JanusInput } from '../janus.js';
import { selectSkillForContestingRoll } from '../select-skill-for-contesting-roll.js';
import { skillRollArr } from '../skill-roll.js';
import { damageTypeRoll } from '../rolls/damage-type-roll.js';
import { attackRoll } from '../rolls/attack-roll.js';
import { preflightPostRollChargeResources, janusPostRollChangeAbilityResources } from '../charges/adjust-charges.js';
import { applyPreRollEvade } from '../reaction-inventory.js';
import { postRollReactionsPreDraw, runPostRollReduce, runPostRollRetaliation } from '../post-roll-reactions.js';
import { checkOpposingTeam } from '../check-opposing-team.js';
import { combatCenter, combatSizeFt } from '../../actions/ability-preconditions.js';
import { position, hasStatus, positionKnown, tremorsenseReaches, roomIdsAt, type PerceptionShape } from '../../../encounter-map/ft3d/perception.js';
import { insideUnion, containingBoxes } from '../../../../../ft3d/engine/ft3d-shape-core.js';
import { attackElevationAdv, spellAttackDirectionalCover } from '../cover/directional-cover.js';
import { posCoord } from '../../../shared/array-types.js';
import { coverDefenseSnapshot, PARTIAL_DEFENSE_DELTA, HEAVY_DEFENSE_DELTA, type CoverTier } from '../../actions/cover-defense.js';
import type { AdvDisSource } from '../../actions/weapon-attack.js';
import { combatCenterDistanceFt as combatRimDistanceFt } from '../../../../../ft3d/engine/ft3d-combat-distance-core.js';
import { activeStatuses, scalar } from '../context.js';
import { criticalHitRangeIncreaseInt, targetIsAutoCriticalForAttackRoll } from '../../port/k3-rules/damage_roll_attack_helpers.js';
import { browserDependencies } from '../../port/browser-dependencies.js';
import { statusItemId } from '../../statuses/expiry.js';
import { readIntegerCompilePath } from '../../compile/integer-compute-slots.js';
import { passiveCritRange } from '../../compile/possessor-compile.js';
import { drawnCreatureVolume } from '../../../shared/physical-creature-body.js';
import { parsePaintedWallSectionSnapshot, paintedWallPhaseVolumes } from '../../../shared/painted-wall-section-state.js';
import { paintedWallProjectileKind, paintedWallWaterPathEffects, WATER_WALL_ID } from '../../../shared/painted-wall-rules.js';
import { statusPaused } from '../../zones/zone-membership.js';

const WEAPON_MELEE = '2FE8DFB7-FE61-4862-AF71-3523262350A0';
const NATURAL_SKILL = '45DE7B42-D548-498A-B7AB-C3DC1C660E57';
const EVADE = 'BE4CCA95-33F0-323D-BA54-844D2540C0F0';
// WeaponType::getId('melee_weapon_type') — janus.php's computed.range=reach injection is melee-only.
const MELEE_WEAPON_TYPE = '1364260D-E37C-4257-AD16-5E745107811A';
// StatusEffect ids (pl-cost-contract.json) — directional_flanking.php / directional_cover.php.
const PRONE = 'F5ACBDB4-D923-3684-57F6-FBF3F720AEFA';
const RESTRAINED = 'E34AF232-5438-88FC-9A9F-73850CCCFD51';
const GRAPPLED = '0C2A8037-B0BC-8300-C818-198881B00E7D';
const PARTIAL_COVER = '3A2FD2B0-28A9-62D3-CB13-A22974618CDB';
const FULL_COVER = 'A126C1D6-FE3E-5578-DCC6-CBBF8742E9C1';
const HUNKERED_DOWN = 'BE6DC590-4EB7-B297-5AC6-8E47AA4E00EF';
const FLANKING_MIN_ANGLE_DEG = 135.0;
const SHAPE_EPS = 1.0e-6;

/** PHP's float-to-string cast after round($x, 1): trailing ".0" is dropped (no fixed decimal). */
function phpRoundedFeet(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  const fixed = rounded.toFixed(1);
  return fixed.endsWith('.0') ? fixed.slice(0, -2) : fixed;
}

/** ft_shape_object_center(): pos_coord(x_pos/y_pos), 0.0 when absent — no AOE-origin fallback. */
function posXY(row: Row): { x: number; y: number } {
  const bucket = record(row.position) ?? record(row.pos_arr) ?? (('x_pos' in row || 'y_pos' in row) ? row : null);
  const num = (v: unknown): number | null => { const s = Array.isArray(v) ? v[0] : v; const n = Number(s); return s !== null && s !== undefined && s !== '' && Number.isFinite(n) ? n : null; };
  return { x: bucket ? (num(bucket.x_pos) ?? 0) : 0, y: bucket ? (num(bucket.y_pos) ?? 0) : 0 };
}

/** ft_combat_center_distance_ft(): BOTH ends keep only their excess-over-5ft size credit (unlike the
 * rim-distance is_within_reach() contract) — the formula is_target_in_range.php actually measures. */
export function combatCenterDistanceFt(a: { x: number; y: number; z: number }, b: { x: number; y: number; z: number }, sizeA: number | null, sizeB: number | null): number {
  const credit = (s: number | null): number => s === null || !Number.isFinite(s) ? 0 : Math.max(0, (s - 5) / 2);
  const half = (s: number | null): number => s === null || !Number.isFinite(s) ? 0 : Math.max(0, s) / 2;
  const dz = Math.max(0, Math.abs(b.z - a.z) - half(sizeA) - half(sizeB));
  const planar = Math.hypot(b.x - a.x, b.y - a.y);
  return Math.max(0, Math.hypot(planar, dz) - credit(sizeA) - credit(sizeB));
}

/** is_within_reach() as the RULES callers use it (flanking, pack tactics, ranged-in-reach DIS). REACH IS THE FRONT-END RULE (owner
 * 2026-09-28, lane RX): ft3d-combat-distance-v3 — the caster's reach ring touching the target's FOOTPRINT, for any size — the same
 * measure the attack's own reach check uses (reachRefusal); PHP's three rule callers pass target_rim: true to match. A sizeless
 * caster is out of reach (PHP's null); a swarm width or a tagged reach the page cannot resolve hands back. */
export function rulesWithinReach(ctx: JanusCtx, caster: Row, target: Row): boolean {
  if (ctx.read.has(caster, 'swarm_width_lanes')) handBack('is_within_reach:swarm-width-not-held');
  const casterSize = combatSizeFt(caster, ctx.read);
  if (casterSize === null) return false;
  const from = combatCenter(caster), to = combatCenter(target);
  if (!from || !to) handBack('is_within_reach:position-not-held');
  const reachRaw = ctx.read.value(caster, 'reach');
  const reach = reachRaw == null || reachRaw === '' ? 5 : Number(reachRaw);
  if (!Number.isFinite(reach)) handBack('is_within_reach:tagged-reach');
  return combatRimDistanceFt(from!, to!, { sourceSizeFeet: casterSize, targetSizeFeet: combatSizeFt(target, ctx.read) }) <= reach;
}
/** ft_flanking_row_can_flank(): public, alive, not prone/restrained/grappled. */
function canFlank(ctx: JanusCtx, row: Row): boolean {
  if (Number(ecLean(row, 'encounter_creature_private') ?? 0) === 1) return false;
  if (Number(ecLean(row, 'encounter_creature_dead') ?? 0) === 1) return false;
  const disqualifying = new Set([PRONE, RESTRAINED, GRAPPLED]);
  for (const status of ctx.read.statuses(row)) {
    const id = upper(itemField(record(status.status_arr), 'item_id') ?? status.status_item_id);
    if (id && disqualifying.has(id)) return false;
  }
  return true;
}

function teamOf(ctx: JanusCtx, row: Row): string {
  const raw = ecLean(row, 'team') ?? ctx.read.value(row, 'team');
  const rec = record(raw);
  return upper(rec?.items ? record(rec.items)?.item_id : raw);
}

/** ft_shape_flanking_angle_deg(): the angle at the target between the caster and the ally. */
function flankingAngleDeg(caster: { x: number; y: number }, target: { x: number; y: number }, ally: { x: number; y: number }): number | null {
  const cx = caster.x - target.x, cy = caster.y - target.y;
  const ax = ally.x - target.x, ay = ally.y - target.y;
  const cl = Math.hypot(cx, cy), al = Math.hypot(ax, ay);
  if (cl <= SHAPE_EPS || al <= SHAPE_EPS) return null;
  const cos = Math.max(-1, Math.min(1, (cx * ax + cy * ay) / (cl * al)));
  return Math.acos(cos) * 180 / Math.PI;
}

/** ft_flanking_context(): melee-only, one ADV stack when an ally of the caster stands roughly opposite
 * the caster across the target (>= FLANKING_MIN_ANGLE_DEG) and both caster and ally can flank. */
function flankingAlly(ctx: JanusCtx, caster: Row, target: Row): Row | null {
  if (!checkOpposingTeam(ctx, caster, target)) return null;
  if (!canFlank(ctx, caster)) return null;
  const casterId = ecId(caster); const targetId = ecId(target);
  if (!casterId || !targetId || casterId === targetId) return null;
  const casterTeam = teamOf(ctx, caster);
  const casterXY = posXY(caster); const targetXY = posXY(target);
  let bestAngle: number | null = null;
  let bestAlly: Row | null = null;
  for (const ally of ctx.members.values()) {
    const allyId = ecId(ally);
    if (!allyId || allyId === casterId || allyId === targetId) continue;
    if (teamOf(ctx, ally) !== casterTeam) continue;
    if (!rulesWithinReach(ctx, ally, target)) continue;
    if (!canFlank(ctx, ally)) continue;
    const angle = flankingAngleDeg(casterXY, targetXY, posXY(ally));
    if (angle === null || angle + 1e-9 < FLANKING_MIN_ANGLE_DEG) continue;
    if (bestAngle === null || angle > bestAngle) { bestAngle = angle; bestAlly = ally; }
  }
  return bestAlly;
}
export function isFlanked(ctx: JanusCtx, caster: Row, target: Row): boolean {
  return flankingAlly(ctx, caster, target) !== null;
}

/** Shared verdict and trace for ordinary swings and movement-owned opportunity strikes. */
export function weaponAttackFlankingSource(ctx: JanusCtx, caster: Row, target: Row): AdvDisSource | null {
  const ally = flankingAlly(ctx, caster, target);
  return ally ? { kind: 'flanking', label: `Flanking — Opposite ${String(ecLean(ally, 'encounter_creature_name') ?? itemField(ally, 'item_name') ?? 'Ally')}`, stacks: 1 } : null;
}

export interface WeaponAttackSpatialContext {
  adv: number;
  sources: AdvDisSource[];
  /** Replace legacy cover-status AC with the measured directional cover. */
  acDelta: number;
  coverTier: CoverTier;
}

function* heldWallCarriers(ctx: JanusCtx): Generator<Row> {
  yield ctx.encounter;
  for (const row of Array.isArray(ctx.encounter.encounter_inventories_arr) ? ctx.encounter.encounter_inventories_arr : []) yield row as Row;
  yield* ctx.members.values();
}

/** Attack-time spatial rules shared by natural-weapon templates and A2, including bound weapon abilities.
 * The template already filtered directed visibility; A2 supplies the page's current visibility reader. */
export function weaponAttackSpatialContext(ctx: JanusCtx, caster: Row, target: Row,
  options: { melee: boolean; throwing?: boolean; weapon?: Row; visibility?: ReturnType<JanusCtx['read']['visibility']> }): WeaponAttackSpatialContext {
  const sources: AdvDisSource[] = [];
  const projectile = !options.melee || options.throwing === true;
  const weapon = options.weapon ?? null;
  const burrowed = positionKnown(target);
  let coverTier: CoverTier = 'none';
  if (burrowed) {
    // Ruling 6: only tremorsense reaches through the enclosing solid. Re-measure at the lowest
    // authored floor above the target; an independent blocker still means total cover.
    const shape = parseShape(ctx), x = posCoord(target, 'x_pos') ?? 0, y = posCoord(target, 'y_pos') ?? 0;
    const feet = posCoord(target, 'z_pos') ?? 0;
    const floors = shape ? containingBoxes(shape, x, y).map(box => Number(box.floor ?? 0)).filter(floor => floor >= feet - SHAPE_EPS) : [];
    if (!tremorsenseReaches(caster, target) || !floors.length) refuse('The line of effect to that target is blocked.');
    const surface = { ...target, position: { ...record(target.position), x_pos: x, y_pos: y, z_pos: Math.min(...floors) } };
    const tier = spellAttackDirectionalCover(ctx, caster, surface, { melee: !projectile, strictBodies: true });
    if (tier === 'blocked') refuse('The line of effect to that target is blocked.');
    coverTier = 'heavy';
  } else {
    if (projectile) coverTier = spellAttackDirectionalCover(ctx, caster, target);
    else checkLineOfEffect(ctx, caster, target);
    if (coverTier === 'blocked') refuse('The line of effect to that target is blocked.');
    const sight = options.visibility === undefined ? ctx.read.visibility(caster, target) : options.visibility;
    if (sight === null) handBack('validate_and_adjust_targets:visibility-unknown');
    if (sight === 'unseen' || sight === 'position_known') refuse('You cannot target into an obscured area.');
    if (sight === 'touch_located') sources.push({ kind: 'environment', label: 'Unseen Target (Located by Touch)', stacks: -1 });
  }
  const projectileKind = paintedWallProjectileKind(upper(ctx.read.value(weapon, 'roll_type')
    ?? (options.melee ? WEAPON_MELEE : '3F85CA97-F9B2-A83A-FDE8-E523FD99346C')),
    upper(itemField(weapon, 'item_id')), upper(ctx.read.value(weapon, 'item_id_used')));
  if (projectile && (projectileKind === 'ordinary' || projectileKind === 'massive')) {
    const point = (row: Row) => {
      const body = drawnCreatureVolume(row, parseShape(ctx), { catalog: ctx.encounter.catalog, position, hasStatus });
      if (!body) handBack('attack_roll:body-not-held');
      return { x: body.x, y: body.y, z: (body.zMin + body.zMax) / 2 };
    };
    const points = [point(caster), point(target)];
    water: for (const holder of heldWallCarriers(ctx)) {
      for (const status of ctx.read.statuses(holder)) {
        if (upper(status.status_origin_id ?? status.status_item_id) !== WATER_WALL_ID
            || Number(status.status_aoe_source ?? 0) !== 1 || statusPaused(status)) continue;
        const snapshot = parsePaintedWallSectionSnapshot(status.status_aoe_geometry);
        if (!snapshot) continue;
        if (paintedWallPhaseVolumes(snapshot.geometry, snapshot.sections).solid.some(wall =>
          paintedWallWaterPathEffects(WATER_WALL_ID, wall, points, projectileKind).weaponDisadvantage)) {
          sources.push({ kind: 'environment', label: 'Wall of Water', stacks: -1 });
          break water;
        }
      }
    }
  }
  const flanking = !projectile ? weaponAttackFlankingSource(ctx, caster, target) : null;
  if (flanking) sources.push(flanking);
  const elevation = attackElevationAdv(ctx, caster, target, !projectile);
  if (elevation) sources.push({ kind: 'elevation', label: elevation > 0 ? 'Higher Ground' : 'Lower Ground', stacks: elevation });
  const pack = ctx.read.value(caster, 'pack_tactics');
  if (options.melee && pack != null && pack !== false && pack !== 0 && pack !== '0' && pack !== '' && ecId(caster) && teamOf(ctx, caster)) {
    for (const member of ctx.members.values()) {
      if (ecId(member) === ecId(caster) || teamOf(ctx, member) !== teamOf(ctx, caster)
          || Number(ecLean(member, 'encounter_creature_dead') ?? 0) !== 0) continue;
      if (rulesWithinReach(ctx, member, target)) { sources.push({ kind: 'passive_ability', label: 'Pack Tactics', stacks: 1 }); break; }
    }
  }
  const enabled = (row: Row | null, key: string): boolean => {
    const v = ctx.read.value(row, key);
    return v != null && v !== false && v !== 0 && v !== '0' && v !== '';
  };
  const defense = coverDefenseSnapshot({ tier: coverTier,
    effectiveDefenseDelta: coverTier === 'partial' ? PARTIAL_DEFENSE_DELTA : coverTier === 'heavy' ? HEAVY_DEFENSE_DELTA : 0,
    baseEc: 0, baseAc: 0, rollTypeId: options.melee ? WEAPON_MELEE : '3F85CA97-F9B2-A83A-FDE8-E523FD99346C',
    sources: [caster, weapon, record(weapon?.attack_ability_arr)].map(row => ({
      rangedWeaponAttacksIgnoreCover: enabled(row, 'ranged_weapon_attacks_ignore_cover'),
      rangedAttacksIgnoreCover: enabled(row, 'ranged_attacks_ignore_cover'),
      rangedAttacksIgnorePartialCover: enabled(row, 'ranged_attacks_ignore_partial_cover'),
    })) });
  return { adv: sources.reduce((sum, source) => sum + source.stacks, 0), sources, coverTier,
    acDelta: defense.environmentalAcDelta - legacyCoverEnvironmentalAcDelta(ctx, target) };
}

/** ft_legacy_cover_environmental_ac_delta(): the AC bonus legacy Partial/Full/Hunkered rows still carry,
 * removed from stored AC so the (melee-always-'none') directional cover result is the sole mechanic. */
export function legacyCoverEnvironmentalAcDelta(ctx: JanusCtx, target: Row): number {
  const ids = new Set([PARTIAL_COVER, FULL_COVER, HUNKERED_DOWN]);
  let delta = 0;
  for (const status of ctx.read.statuses(target)) {
    const def = record(status.status_arr);
    const id = upper(itemField(def, 'item_id') ?? status.status_item_id);
    if (!ids.has(id)) continue;
    const mod = ctx.read.value(def, 'ac_mod_environmental');
    delta += mod === null || mod === undefined ? 0 : requiredInt(mod, 'attack_roll:ac-mod-environmental-not-held');
  }
  return delta;
}

/** Object projectiles use their authored maximum range; native melee weapons extend the caster's reach. */
export function reachRefusal(ctx: JanusCtx, caster: Row, target: Row, weapon: Row): string | null {
  if (weapon._ftObjectWeaponAttack === true && (weapon._ftObjectWeaponThrow === true
      || upper(ctx.read.value(weapon, 'roll_type')) === '3F85CA97-F9B2-A83A-FDE8-E523FD99346C')) {
    const from = combatCenter(caster), to = combatCenter(target), size = combatSizeFt(caster, ctx.read);
    if (!from || !to || size === null) handBack('attack_roll:range-geometry-not-held');
    const multiplier = Number(ctx.read.value(caster, 'weapon_ranged_attack_range_multiplier') ?? 0);
    const maximum = Number(ctx.read.value(weapon, 'range_end')) * (weapon._ftObjectWeaponThrow !== true && multiplier > 1 ? multiplier : 1);
    if (!Number.isFinite(maximum) || maximum <= 0) handBack('attack_roll:range-not-held');
    const distance = combatCenterDistanceFt(from!, to!, size, combatSizeFt(target, ctx.read));
    return distance <= maximum ? null : `Target is out of range by ${phpRoundedFeet(distance - maximum)} ft.`;
  }
  const weaponType = upper(ctx.read.value(weapon, 'weapon_type'));
  if (weaponType !== MELEE_WEAPON_TYPE) return null;
  const weaponReachRaw = ctx.read.value(weapon, 'weapon_reach');
  if (weaponReachRaw === null || weaponReachRaw === undefined || weaponReachRaw === '' || weaponReachRaw === 0 || weaponReachRaw === '0')
    return null; // !empty(weapon_reach) failed -> computed.range never set -> is_target_in_range never runs
  if (ctx.read.has(caster, 'swarm_width_lanes') || ctx.read.has(target, 'swarm_width_lanes')) handBack('attack_roll:reach-swarm-not-modeled');
  const baseReachRaw = ctx.read.value(caster, 'reach');
  const baseReach = baseReachRaw === null || baseReachRaw === undefined || baseReachRaw === ''
    ? 5 : requiredInt(baseReachRaw, 'attack_roll:reach-not-held');
  const weaponReachExt = Math.max(0, requiredInt(weaponReachRaw, 'attack_roll:weapon-reach-not-held') - 5);
  const effectiveReach = baseReach + weaponReachExt;
  const casterSize = combatSizeFt(caster, ctx.read);
  const targetSize = combatSizeFt(target, ctx.read);
  if (casterSize === null) handBack('attack_roll:caster-size-not-held');
  const casterPos = combatCenter(caster); const targetPos = combatCenter(target);
  if (!casterPos || !targetPos) handBack('attack_roll:position-not-held');
  // REACH IS THE FRONT-END RULE (owner 2026-09-28, lane RX): ft3d-combat-distance-v3 — the caster's reach ring (its reach + its own
  // size credit) only has to TOUCH the target's footprint, for any creature size. The approach planner seats the mover by this same
  // rule; the old v2 centre-minus-credits measure refused a Large mover's own approach seat ("out of reach by 1.9 ft", BUG LEDGER 09-28 RX).
  // PHP's legacy reach branch (is_target_in_range.php) measures the same rim distance since the same change.
  const distance = combatRimDistanceFt(casterPos!, targetPos!, { sourceSizeFeet: casterSize, targetSizeFeet: targetSize });
  if (distance <= effectiveReach) return null;
  return `Target is out of reach by ${phpRoundedFeet(distance - effectiveReach)} ft.`;
}

/** ft_encounter_shape(): the encounter's stored shape blob (JSON string or already-decoded object), or
 * null when unpromoted/absent — matching PHP's `is_array($raw)` guard exactly (visibility.ts twin). */
function parseShape(ctx: JanusCtx): PerceptionShape | null {
  let shape: unknown = record(ctx.encounter.encounters)?.encounter_shape;
  if (shape === null || shape === undefined) return null;
  if (typeof shape === 'string') {
    try { shape = JSON.parse(shape); } catch { return null; }
  }
  const candidate = record(shape);
  if (!candidate || !Array.isArray(candidate.boxes)) return null;
  return candidate as unknown as PerceptionShape;
}

/** ft_spatial_action_geometry_context()'s non-ranged (melee/direct-target) line-of-effect half. */
export function checkLineOfEffect(ctx: JanusCtx, caster: Row, target: Row): void {
  const shape = parseShape(ctx);
  if (!shape) return; // ft_encounter_shape() null -> $none(): lineOfEffect stays 'clear'
  const from = posXY(caster); const to = posXY(target);
  if (!insideUnion(shape, from.x, from.y) || !insideUnion(shape, to.x, to.y)) return; // outside union -> $none(true), still clear
  const fromRooms = roomIdsAt(shape, { x: from.x, y: from.y, z: 0 });
  const toRooms = roomIdsAt(shape, { x: to.x, y: to.y, z: 0 });
  if (fromRooms.some((id) => toRooms.includes(id))) return; // same room: convex — its own walls cannot block its own interior
  handBack('attack_roll:line-of-effect-cross-room-not-held');
}

/** calc_total_damage_and_apply_to_target.php's EC-tier dr_total: `layer_value(target, 'dr_total')`, which bridges to
 * computed.creature.ac.dr on a fresh blob (the spell attack path reads it the same way). */
export function targetDamageReduction(ctx: { read: { value: (row: Row, key: string) => unknown } }, target: Row): number {
  const raw = ctx.read.value(target, 'dr_total');
  const projected = record(record(record(target.computed)?.creature)?.ac)?.dr;
  return raw == null ? (projected == null ? 0 : requiredInt(projected, 'attack_roll:invalid-dr')) : requiredInt(raw, 'attack_roll:invalid-dr');
}

/** Native automatic criticals: Paralyzed / Unconscious on any connected attack, Dying on melee.
 * Use the same port as the weapon recorder, including pending same-use statuses. */
export function deathTargetAutoCritical(ctx: JanusCtx, target: Row, rollType: string): boolean {
  const held = new Set(activeStatuses(ctx, target).map(statusItemId));
  return targetIsAutoCriticalForAttackRoll(target, rollType, browserDependencies({
    has_status_effect: (_row: unknown, id: string) => held.has(id),
    layer_has: (row: Row, key: string) => ctx.read.has(row, key),
    layer_value: (row: Row, key: string) => ctx.read.value(row, key),
  }));
}

/** Critical house-rule cases that still require held state, before any dice. */
export function critGates(ctx: JanusCtx, target: Row): void {
  const rules = ctx.read.houseRules?.();
  if (!rules) handBack('check_critical_hit_logic:house-rules-not-held');
  if (rules.maimed && ctx.read.statuses(target).some((s) => upper(s.status_item_id) === 'A5CDC77C-D224-DD5F-291B-EF8DF2EA443D'
      && Number(s.status_stack ?? 1) >= 9)) handBack('check_critical_hit_logic:maimed-overflow');
}

/** The layer reader already bridges the compiled status/gear total. Never add its
 * legacy root copy or walk the status definitions again. The separate bucket-passive
 * accumulator contributes only its share not already attributed to a folded source. */
export function attackCriticalHitThreshold(ctx: { read: { value(row: Row, key: string): unknown;
  entries?(row: Row, key: string): unknown[]; definition?(id: string): Row | null;
  statuses?(row: Row): Row[]; statusRows?(row: Row): Row[] } }, caster: Row, ability: Row, variable?: unknown): number {
  const declared = scalar(ctx.read.value(ability, 'critical_hit_range_increase_mod'));
  let passive = criticalHitRangeIncreaseInt(scalar(ctx.read.value(caster, 'critical_hit_range_increase_mod_from_passives')));
  const sources = readIntegerCompilePath(caster, ['computed', 'creature', 'modifier_sources', 'critical_hit_range_increase_mod']);
  if (passive > 0 && Array.isArray(sources) && sources.length && ctx.read.entries && ctx.read.definition) {
    const contributions = new Map<string, number>();
    // Reuse the compile's own inactive/feat/aura exclusions. An unrelated root
    // contribution must survive when a non-counted bucket copy shares the status id.
    const counted = passiveCritRange({ passive_ability: ctx.read.entries(caster, 'passive_ability'), feat: ctx.read.entries(caster, 'feat') }, ctx.read.definition,
      { layerValue: (row, key) => ctx.read.value(row as Row, key), contribution: (id, value) => contributions.set(upper(id), (contributions.get(upper(id)) ?? 0) + value) });
    if (typeof counted === 'string') handBack('attack:critical-range:' + counted);
    const statuses = ctx.read.statuses?.(caster) ?? ctx.read.statusRows?.(caster) ?? [];
    for (const raw of sources) {
      const source = record(raw);
      if (!source) continue;
      const id = upper(source.href_id);
      const providerId = source.category === 'passive_ability' ? id : source.category === 'status_effect'
        ? statusItemId(statuses.find(status => upper(ctx.read.value(status, 'status_id')) === id) ?? {}) : '';
      const overlap = Math.min(contributions.get(providerId) ?? 0, criticalHitRangeIncreaseInt(source.value));
      if (overlap) {
        passive = Math.max(0, passive - overlap);
        contributions.set(providerId, (contributions.get(providerId) ?? 0) - overlap);
      }
    }
  }
  const increase = criticalHitRangeIncreaseInt(scalar(ctx.read.value(caster, 'critical_hit_range_increase_mod')))
    + passive
    + criticalHitRangeIncreaseInt(typeof declared === 'string' && ['[x]', '+[x]'].includes(declared.trim()) ? scalar(variable) : declared);
  return 20 - Math.min(20, increase);
}

/** Positive weapon_attack − extra_attack supplies that many DIS stacks. Native swings
 * read the live counter; ability rolls use their compiled hindered_attack_counter. */
export function hinderedAttackStacksFromCounter(counter: unknown): number {
  return Math.max(0, requiredInt(counter ?? 0, 'attack_roll:hindered-attack-counter-invalid'));
}

export function hinderedAttackStacks(ctx: JanusCtx, caster: Row): number {
  if (!ctx.read.has(caster, 'hindered_attack_counter')) return 0;
  return hinderedAttackStacksFromCounter(ctx.read.value(caster, 'hindered_attack_counter'));
}

/** template_weapon_attack_roll.php: weapon phase, with damage_roll.php's missed branch. */
export function templateWeaponAttackRoll(ctx: JanusCtx, input: JanusInput, weapon: Row, caster: Row, targets: Row[]): void {
  const type = String(itemField(weapon, 'item_type') ?? '');
  if (targets.length === 1 && isInventoryRow(targets[0]!) && ['weapon', 'natural_weapon'].includes(type)) {
    const target = targets[0]!;
    const rangeMessage = reachRefusal(ctx, caster, target, weapon);
    if (rangeMessage) refuse(rangeMessage);
    checkLineOfEffect(ctx, caster, target);
    const ability = record(weapon.attack_ability_arr) ?? weapon;
    preflightPostRollChargeResources(ctx, ability, caster, input.useAbilityVariable1);
    // Inventory hits auto-connect in the shared attack phase. Its damage path owns
    // typed defences, wall carving, destruction and the ordinary inventory receipt.
    attackRoll(ctx, input, weapon, caster, targets, { ability, skill: '' });
    spendWeaponAction(ctx, weapon, caster);
    janusPostRollChangeAbilityResources(ctx, ability, caster, input.useAbilityVariable1, true, input);
    return;
  }
  if (type !== 'natural_weapon') handBack('template_weapon_attack_roll:weapon-phase');
  // template_weapon_attack_roll.php ~L55: a weapon / natural weapon strike with more than one target and no attack-ability context is refused
  // with PHP's own words (display_warning + exit — lane A8, R-108 `target-count`, was a hand-back). Today's PHP: R-103 moved the player's target
  // COUNT to the browser cast gate (cast-range-gate.ts targetCountRefusal), so validate_and_adjust_targets no longer vetoes a player cast first
  // (every post-R-103 capture logs this exit's message; the 09-26 pre-R-103 variants were recaptured — overlay …-a8count).
  if (targets.length > 1 && !(record(weapon.attack_ability_arr) && Object.keys(record(weapon.attack_ability_arr)!).length))
    refuse('Weapon Attack Rolls can only have 1 target. Please select just 1 target and try again.');
  if (targets.length !== 1) handBack('template_weapon_attack_roll:target-count');
  const target = targets[0]!;
  // A creature attacking ITSELF without a compiled reaction carrier (computed.creature.abilities is PC-only): PHP's pre-roll
  // trigger pass fires Evade from the creature's root grants and the attack then rolls DIS2 (a real capture — Claw on its own
  // wielder); applyPreRollEvade now fires that Evade too (the janus_trigger twin, 2026-09-30) but yields DIS1 — the second stack of a
  // self-aimed Evade is unported. A PC's self-attack reads its compiled carrier and matches. State, pre-draw.
  if (ecId(target) && ecId(target) === ecId(caster)
      && !Array.isArray(record(record(record(target.computed)?.creature)?.abilities)?.reaction_ability))
    handBack('template_weapon_attack_roll:self-target-without-reaction-carrier');
  const evaded = applyPreRollEvade(ctx, target, WEAPON_MELEE, caster);
  postRollReactionsPreDraw(ctx, caster, target, WEAPON_MELEE);
  // validate_and_adjust_targets.php's range gate + AUTHORITATIVE SPATIAL GATE precede janus() dispatch
  // in PHP; ported here since this engine has no earlier hook for a direct (non-AOE) single target.
  const rangeMessage = reachRefusal(ctx, caster, target, weapon);
  if (rangeMessage) refuse(rangeMessage);
  const spatial = weaponAttackSpatialContext(ctx, caster, target, { melee: true, weapon,
    visibility: target._ft_touch_located_attack_target ? 'touch_located' : 'visible' });
  if (ctx.read.has(weapon, 'passive_ability')) handBack('damage_roll:weapon-passive-not-modeled');
  // weapon_essence (determine_attack_roll_skill_roll.php: the attack's essence_mod + the damage row's essence face) is not ported → PHP.
  if (ctx.read.has(caster, 'weapon_essence')) handBack('attack_roll:weapon-modifiers:weapon-essence');
  const skill = selectSkillForContestingRoll(ctx, caster, [NATURAL_SKILL]);
  // attack_roll.php ~L42 (lane A5, 2026-10-01 — playtest 09-30 `attack_roll:weapon-modifiers` ×8): a later weapon attack this turn
  skill.adv -= hinderedAttackStacks(ctx, caster);
  if (evaded) skill.adv -= 1;
  skill.adv += spatial.adv;
  const dc = Math.max(0, requiredInt(ctx.read.value(target, 'ec_total'), 'attack_roll:ec-not-held'));
  critGates(ctx, target);
  const criticalThreshold = attackCriticalHitThreshold(ctx, caster, record(weapon.attack_ability_arr) ?? weapon, input.useAbilityVariable1);
  // Everything that decides WHETHER this swing can be served is settled BEFORE the d20: a hand-back after seeing the roll
  // would let PHP re-roll it (the hit riders, the AC / damage-reduction reads).
  if (ctx.read.has(weapon, 'non_lethal_attack') || ctx.read.has(weapon, 'extra_damage_roll')
      || ctx.read.has(weapon, 'passive_ability')) handBack('damage_roll:weapon-hit-riders');
  // get_damage_application_info.php / damage_roll_attack_helpers.php: an auto-hit applies damage below the DC and forces
  // critical_hit = 0; an auto-miss (underwater included) forces the miss; hit_to_crit / crit_to_hit rewrite the tier. Read on
  // the weapon, its nested attack-ability context, and the target — the spell attack path's own gate list.
  const attackContext = record(weapon.attack_ability_arr);
  for (const key of ['attack_automatically_hits', 'attack_automatically_misses', 'attack_underwater_auto_miss', 'hit_to_crit', 'crit_to_hit'])
    if (ctx.read.has(weapon, key) || ctx.read.has(target, key) || (attackContext && ctx.read.has(attackContext, key)))
      handBack(`damage_roll:weapon-${key}`);
  const acRaw = ctx.read.value(target, 'ac_total');
  if (acRaw == null) handBack('damage_roll:invalid-ac');
  const storedAc = Math.max(dc, requiredInt(acRaw, 'damage_roll:invalid-ac'));
  const baseAc = Math.max(dc, storedAc + spatial.acDelta);
  const damageReduction = targetDamageReduction(ctx, target);
  let natural = 0;
  const result = skillRollArr(ctx, input, caster, skill, target, dc, { rollType: WEAPON_MELEE, criticalThreshold, onNatural: (face) => { natural = face; } });
  // A natural 1 always misses; a connected hit uses the shared critical range.
  if (natural !== 1 && result >= dc) {
    runPostRollReduce(ctx, caster, target, WEAPON_MELEE);
    // damage_type_rolls.php: the hit is 'AC' when the roll meets the effective AC OR it is a critical; otherwise 'EC', and
    // calc_total_damage_and_apply_to_target subtracts the target's dr_total.
    const critical = natural >= criticalThreshold || deathTargetAutoCritical(ctx, target, WEAPON_MELEE);
    const ecTier = !critical && result < baseAc;
    damageTypeRoll(ctx, input, weapon, caster, target, false, true, ecTier ? damageReduction : 0, critical);
  } else insertRoll(ctx, { roll_type_1: 'weapon_attack_missed', roll_item_id: input.abilityId, by: caster, to: target });
  runPostRollRetaliation(ctx, caster, target, WEAPON_MELEE, natural !== 1 && result >= dc);
  // template_weapon_attack_roll.php pays for the swing after damage_roll, including a miss.
  spendWeaponAction(ctx, weapon, caster);
}

function spendWeaponAction(ctx: JanusCtx, weapon: Row, caster: Row): void {
  if (ctx.read.inCombat(ctx.encounter, caster)) {
    const casterEc = ec(caster);
    if (upper(ctx.read.value(weapon, 'equipment_slot')) === '3805BDD7-3461-DDDB-F445-922A5A992987')
      casterEc.head_attack = 1;
    casterEc.aps = requiredInt(ecLean(caster, 'aps'), 'template_weapon_attack_roll:aps-not-held') - 1;
    casterEc.weapon_attack = requiredInt(ecLean(caster, 'weapon_attack'), 'template_weapon_attack_roll:weapon-attack-not-held') + 1;
  }
}
